import { useState, useEffect, useCallback } from 'react';

const NOTIFICATION_STORAGE_KEY = 'evolve-notifications-enabled';
const NOTIFICATION_DECLINED_KEY = 'evolve-notifications-declined';
const NOTIFICATION_PROMPT_COUNT_KEY = 'evolve-notifications-prompt-count';

// Motivational quotes for push notifications
const MOTIVATIONAL_QUOTES = [
  { quote: "The only bad workout is the one that didn't happen", cta: "Let's go!" },
  { quote: "Pain is temporary, glory is forever", cta: "Time to evolve." },
  { quote: "You don't find willpower, you create it", cta: "Create it now." },
  { quote: "Champions are made when no one is watching", cta: "Be a champion." },
  { quote: "Your future self will thank you", cta: "Start now." },
  { quote: "Discipline is choosing what you want most over what you want now", cta: "Choose greatness." },
  { quote: "The body achieves what the mind believes", cta: "Believe today." },
  { quote: "Every rep counts. Every day matters", cta: "Make it count." },
  { quote: "Strong is not given. It's earned", cta: "Earn it." },
  { quote: "Comfort zone? That's where dreams go to die", cta: "Break free." },
  { quote: "You're one workout away from a better mood", cta: "Let's move." },
  { quote: "Legends don't skip days", cta: "Be legendary." },
];

export function useNotifications() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [hasDeclined, setHasDeclined] = useState(false);
  const [promptCount, setPromptCount] = useState(0);

  useEffect(() => {
    // Check if notifications are supported
    const supported = 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      // Check current permission state
      if (Notification.permission === 'granted') {
        setIsEnabled(true);
        localStorage.setItem(NOTIFICATION_STORAGE_KEY, 'true');
      } else if (Notification.permission === 'denied') {
        setHasDeclined(true);
        localStorage.setItem(NOTIFICATION_DECLINED_KEY, 'true');
      }
    }
    
    // Load prompt count
    const count = parseInt(localStorage.getItem(NOTIFICATION_PROMPT_COUNT_KEY) || '0', 10);
    setPromptCount(count);
  }, []);

  const getRandomQuote = useCallback(() => {
    return MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)];
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setIsEnabled(true);
        setHasDeclined(false);
        localStorage.setItem(NOTIFICATION_STORAGE_KEY, 'true');
        localStorage.removeItem(NOTIFICATION_DECLINED_KEY);
        
        // Show welcome notification with motivational quote
        const { quote } = getRandomQuote();
        new Notification('🔥 Evolve Notifications Active', {
          body: `"${quote}" — You're 4.7× more likely to hit Immortal now.`,
          icon: '/favicon.ico',
        });
        
        return true;
      } else {
        setHasDeclined(true);
        localStorage.setItem(NOTIFICATION_DECLINED_KEY, 'true');
        
        // Increment prompt count
        const newCount = promptCount + 1;
        setPromptCount(newCount);
        localStorage.setItem(NOTIFICATION_PROMPT_COUNT_KEY, newCount.toString());
        
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported, promptCount, getRandomQuote]);

  const sendNotification = useCallback((title: string, body: string) => {
    if (!isEnabled || !isSupported) return;
    
    new Notification(title, {
      body,
      icon: '/favicon.ico',
    });
  }, [isEnabled, isSupported]);

  // Send a motivational notification with quote
  const sendMotivationalNotification = useCallback((customMessage?: string) => {
    if (!isEnabled || !isSupported) return;
    
    const { quote, cta } = getRandomQuote();
    
    new Notification('💪 Gym Time', {
      body: customMessage 
        ? `"${quote}" — ${customMessage}`
        : `"${quote}" — ${cta}`,
      icon: '/favicon.ico',
    });
  }, [isEnabled, isSupported, getRandomQuote]);

  const shouldPromptAgain = useCallback(() => {
    // Always prompt if not enabled and not permanently declined
    return isSupported && !isEnabled && Notification.permission !== 'denied';
  }, [isSupported, isEnabled]);

  return {
    isEnabled,
    isSupported,
    hasDeclined,
    promptCount,
    requestPermission,
    sendNotification,
    sendMotivationalNotification,
    shouldPromptAgain,
    getRandomQuote,
  };
}
